import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { AdminHeader, AdminBody } from "./components/AdminHeader";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScanLine, CheckCircle2, XCircle, AlertTriangle, UserCheck, Search, Camera } from "lucide-react";

const StatBox = ({ label, value, tone }) => (
  <div className={`p-5 border ${tone || "border-f2b-filet bg-white"}`} data-testid={`checkin-stat-${label}`}>
    <div className="f2b-kicker text-f2b-gray">{label}</div>
    <div className="font-display font-bold text-3xl mt-1">{value}</div>
  </div>
);

const CheckinAdmin = () => {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [editionId, setEditionId] = useState("");
  const [q, setQ] = useState("");
  const [camOn, setCamOn] = useState(false);
  const camRef = useRef(null);
  const html5Ref = useRef(null);

  const stopCam = async () => {
    try { if (html5Ref.current) { await html5Ref.current.stop(); await html5Ref.current.clear(); } } catch {}
    html5Ref.current = null;
    setCamOn(false);
  };

  useEffect(() => () => { stopCam(); }, []);

  const startCam = async () => {
    setCamOn(true);
    setTimeout(async () => {
      try {
        const inst = new Html5Qrcode("qr-reader");
        html5Ref.current = inst;
        await inst.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
          async (decoded) => { await stopCam(); scan(decoded); },
          () => {});
      } catch (e) {
        toast.error("Impossible d'accéder à la caméra. Utilisez la recherche manuelle.");
        setCamOn(false);
      }
    }, 100);
  };

  const { data: editions = [] } = useQuery({ queryKey: ["admin-editions"], queryFn: async () => (await api.get("/admin/editions")).data });
  const { data: dash } = useQuery({ queryKey: ["dashboard", editionId], queryFn: async () => (await api.get(`/admin/dashboard${editionId ? `?edition_id=${editionId}` : ""}`)).data, refetchInterval: 5000 });
  const { data: results = [] } = useQuery({
    queryKey: ["checkin-search", editionId, q],
    queryFn: async () => (await api.get(`/admin/checkin/search?edition_id=${editionId}&q=${encodeURIComponent(q)}`)).data,
    enabled: q.length > 1 || !!editionId,
  });

  const scan = async (value) => {
    const v = (value ?? code).trim();
    if (!v) return;
    try {
      const res = await api.post("/admin/checkin/scan", { code: v });
      setResult(res.data);
      if (res.data.ok) toast.success(res.data.message);
      else toast.error(res.data.message);
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["checkin-search"] });
    } catch (e) {
      setResult({ ok: false, message: e?.response?.data?.detail || "QR code invalide." });
      toast.error(e?.response?.data?.detail || "QR code invalide.");
    }
    setCode("");
    inputRef.current?.focus();
  };

  const markPresent = async (p) => {
    try {
      await api.put(`/admin/registrations/${p.id}/status`, { status: "Présent", cascade_group: false });
      toast.success(`${p.first_name} ${p.last_name} — présent`);
      qc.invalidateQueries({ queryKey: ["checkin-search"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch { toast.error("Erreur"); }
  };

  return (
    <>
      <AdminHeader title="Check-in" subtitle="Accueil le jour de l'événement — scan des billets" />
      <AdminBody>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatBox label="Confirmés" value={dash?.confirmed ?? "—"} tone="border-f2b-cyan bg-f2b-cyan/5" />
          <StatBox label="Arrivés" value={dash?.arrived ?? "—"} tone="border-green-200 bg-green-50" />
          <StatBox label="Absents" value={dash?.absent ?? "—"} />
          <StatBox label="Liste d'attente" value={dash?.waitlist ?? "—"} />
          <StatBox label="Taux présence" value={`${dash?.attendance_rate ?? 0}%`} tone="border-f2b-black bg-f2b-black text-white" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* scanner */}
          <div>
            <h2 className="f2b-kicker text-f2b-gray mb-3 flex items-center gap-2"><ScanLine size={16} /> Scanner un billet</h2>
            <div className="bg-white border border-f2b-filet p-6">
              <p className="text-sm text-f2b-gray mb-3">Scannez le QR code (ou collez le code / lien) puis Entrée.</p>
              <div className="flex gap-2">
                <Input ref={inputRef} autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scan(); } }}
                  placeholder="Code du billet…" data-testid="checkin-input" />
                <button onClick={() => scan()} className="bg-f2b-cyan text-f2b-black px-5 text-sm font-medium" data-testid="checkin-scan-btn">Valider</button>
              </div>
              <button onClick={camOn ? stopCam : startCam} data-testid="checkin-camera-btn"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-f2b-black px-4 py-2.5 text-sm hover:bg-f2b-black hover:text-white transition-colors">
                <Camera size={16} /> {camOn ? "Arrêter la caméra" : "Scanner un QR"}
              </button>
              <div id="qr-reader" ref={camRef} className={`mt-3 overflow-hidden ${camOn ? "block" : "hidden"}`} data-testid="qr-reader" />

              {result && (
                <div className={`mt-5 p-5 border-l-4 ${result.ok ? "border-green-500 bg-green-50" : result.already ? "border-f2b-gold bg-f2b-gold/10" : "border-red-500 bg-red-50"}`} data-testid="checkin-result">
                  <div className="flex items-center gap-2 font-medium">
                    {result.ok ? <CheckCircle2 className="text-green-600" size={20} /> : result.already ? <AlertTriangle className="text-f2b-gold" size={20} /> : <XCircle className="text-red-500" size={20} />}
                    {result.message}
                  </div>
                  {result.participant && (
                    <div className="mt-2 text-sm text-f2b-text">
                      <strong>{result.participant.first_name} {result.participant.last_name}</strong>
                      {result.participant.company ? ` — ${result.participant.company}` : ""}<br />
                      Billet {result.participant.ticket_number} · {result.participant.profile_type}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* manual search */}
          <div>
            <h2 className="f2b-kicker text-f2b-gray mb-3 flex items-center gap-2"><UserCheck size={16} /> Recherche manuelle</h2>
            <div className="bg-white border border-f2b-filet p-6">
              <div className="flex gap-2 mb-4">
                <Select value={editionId || "all"} onValueChange={(v) => setEditionId(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-40" data-testid="checkin-edition"><SelectValue placeholder="Édition" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes éditions</SelectItem>
                    {editions.map((e) => <SelectItem key={e.id} value={e.id}>{e.date_label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-f2b-gray" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail, billet…" className="pl-9" data-testid="checkin-search-input" />
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto divide-y divide-f2b-filet">
                {results.length === 0 ? <p className="text-f2b-gray text-sm py-6 text-center">Aucun résultat.</p> :
                  results.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-3" data-testid={`checkin-row-${p.id}`}>
                      <div>
                        <p className="font-medium text-f2b-text">{p.first_name} {p.last_name}</p>
                        <p className="text-xs text-f2b-gray">{p.company} · {p.ticket_number}</p>
                      </div>
                      {p.status === "Présent" ? (
                        <span className="text-green-600 text-sm flex items-center gap-1"><CheckCircle2 size={16} /> Présent</span>
                      ) : (
                        <button onClick={() => markPresent(p)} className="text-sm border border-f2b-filet px-3 py-1.5 hover:border-f2b-cyan" data-testid={`mark-present-${p.id}`}>Marquer présent</button>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </AdminBody>
    </>
  );
};

export default CheckinAdmin;
